import React, { useState, useEffect, useCallback } from 'react';
import { updateUsuario } from '../../../api/usuarios';
import { Button, TextField, Box, CircularProgress, MenuItem, Checkbox, Autocomplete } from '@mui/material';
import { getInstaladores } from '../../../api/instaladores';
import { getClientes } from '../../../api/clientes';

interface Instalador {
  id: number;
  nombre: string;
}

interface ClienteOpcion {
  id: number;
  nombre: string;
}

const EditUsuario = React.memo(({ usuario, onClose, LstPerfiles }: any) => {
  const [instalador, setInstalador] = useState<Instalador[]>([]);
  const [clientes, setClientes] = useState<ClienteOpcion[]>([]);
  const [formData, setFormData] = useState(usuario);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    Us_In_Id: '',
    Us_Cl_Id: '',
    Us_Nombre: '',
    Us_Usuario: '',
    Us_Perfil_Id: '',
    // Us_Contrasena: '',
  });

  const perfilSeleccionado = LstPerfiles.find((p: any) => p.Pe_Id === formData.Us_Perfil_Id);
  const esPerfilCliente = perfilSeleccionado?.Pe_Nombre === 'Cliente';

  const fetchInstaladores = useCallback(async () => {
    try {
      const response = await getInstaladores();
      if (response.isOk && response.data) {
        const mappedInstaladores = response.data.map((ins: any) => ({
          id: ins.In_Id,
          nombre: ins.In_Nombre,
        }));
        setInstalador(mappedInstaladores);
      } else {
        setInstalador([]);
      }
    } catch (error) {
      console.error('Error inesperado al cargar los instaladores:', error);
    }
  }, []);

  const fetchClientes = useCallback(async () => {
    try {
      const response = await getClientes();
      if (response.isOk && response.data) {
        setClientes(response.data.map((c: any) => ({ id: c.Cl_Id, nombre: c.Cl_Nombre })));
      } else {
        setClientes([]);
      }
    } catch (error) {
      console.error('Error inesperado al cargar los clientes:', error);
    }
  }, []);

  useEffect(() => {
    fetchInstaladores();
    fetchClientes();
  }, [fetchInstaladores, fetchClientes]);

  useEffect(() => {
    setFormData(usuario);
  }, [usuario]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    let tempErrors = { Us_Nombre: '', Us_Usuario: '', Us_Perfil_Id: '', Us_In_Id: '', Us_Cl_Id: '' };
    if (!formData.Us_Nombre) tempErrors.Us_Nombre = 'El nombre es requerido';
    if (!formData.Us_Usuario) tempErrors.Us_Usuario = 'El usuario es requerido';
    // if (!formData.Us_Contrasena) tempErrors.Us_Contrasena = 'La contraseña es requerida';
    if (!formData.Us_Perfil_Id) tempErrors.Us_Perfil_Id = 'El perfil es requerido';
    if (formData.Us_EsInstalador && !formData.Us_In_Id) tempErrors.Us_In_Id = 'El instalador es requerido';
    if (esPerfilCliente && !formData.Us_Cl_Id) tempErrors.Us_Cl_Id = 'El cliente es requerido';
    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    await updateUsuario(formData.Us_Id, { ...formData, Us_Cl_Id: esPerfilCliente ? formData.Us_Cl_Id : null });
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    if (!formData.Us_EsInstalador) {
      setFormData((prev: any) => ({ ...prev, Us_In_Id: 0 }));
  }}, [formData.Us_EsInstalador]);

  useEffect(() => {
    // Solo limpia Us_Cl_Id cuando ya se resolvio el perfil y se confirma que NO es Cliente --
    // evita una carrera donde LstPerfiles aun no cargo y se borre el Cl_Id de un usuario cliente
    // existente antes de que el usuario vea el formulario.
    if (perfilSeleccionado && !esPerfilCliente) {
      setFormData((prev: any) => ({ ...prev, Us_Cl_Id: 0 }));
    }
  }, [esPerfilCliente, perfilSeleccionado]);

  return (
    <div>
      <form>
        <TextField label='Nombre' name='Us_Nombre' value={formData.Us_Nombre} onChange={handleChange} fullWidth required sx={{ marginBottom: '0.8rem', marginTop: '1rem' }} size='small' error={!!errors.Us_Nombre} helperText={errors.Us_Nombre} />
        <TextField label='Usuario' name='Us_Usuario' value={formData.Us_Usuario} onChange={handleChange} fullWidth required sx={{ marginBottom: '0.8rem' }} size='small' error={!!errors.Us_Usuario} helperText={errors.Us_Usuario} />
        <TextField select label='Perfil' name='Us_Perfil_Id' value={formData.Us_Perfil_Id} onChange={handleChange} fullWidth required sx={{ marginBottom: '0.8rem' }} size='small' error={!!errors.Us_Perfil_Id} helperText={errors.Us_Perfil_Id}>
          {LstPerfiles.map((perfil: any) => (
            <MenuItem key={perfil.Pe_Id} value={perfil.Pe_Id}>
              {perfil.Pe_Nombre}
            </MenuItem>
          ))}
        </TextField>
        {/* <TextField label='Contraseña' name='Us_Contrasena' type='password' value={formData.Us_Contrasena} onChange={handleChange} fullWidth required sx={{ marginBottom: '0.8rem' }} size='small' /> */}
        <Box display="flex" alignItems="center">
          <Checkbox
            checked={formData.Us_EsInstalador}
            onChange={(e) => setFormData({ ...formData, Us_EsInstalador: e.target.checked })}
          />
          <span>¿Es instalador?</span>
        </Box>
        <Autocomplete
          size='small'
          options={instalador}
          disabled={!formData.Us_EsInstalador}
          getOptionLabel={(option) => option.nombre}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={instalador.find((p) => p.id === formData.Us_In_Id) || null}
          onChange={(event, value) => setFormData({ ...formData, Us_In_Id: value ? value.id : 0 })}
          renderInput={(params) => <TextField {...params} label="Instalador" variant="outlined" fullWidth sx={{ marginBottom: '0.8rem' }}
            error={!!errors.Us_In_Id}
            helperText={errors.Us_In_Id}
          />}
        />
        {esPerfilCliente && (
          <Autocomplete
            size='small'
            options={clientes}
            getOptionLabel={(option) => option.nombre}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={clientes.find((c) => c.id === formData.Us_Cl_Id) || null}
            onChange={(event, value) => setFormData({ ...formData, Us_Cl_Id: value ? value.id : 0 })}
            renderInput={(params) => <TextField {...params} label="Cliente" variant="outlined" fullWidth sx={{ marginBottom: '0.8rem' }}
              error={!!errors.Us_Cl_Id}
              helperText={errors.Us_Cl_Id || 'Este usuario podrá iniciar sesión y ver únicamente sus propios Preservicios'}
            />}
          />
        )}
        <Box display='flex' justifyContent='space-between'>
          <Button onClick={handleSubmit} variant='outlined' size='small' disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Actualizar'}
          </Button>
          <Button onClick={onClose} variant='outlined' color='error' size='small'>
            Cancelar
          </Button>
        </Box>
      </form>
    </div>
  );
});

export default EditUsuario;
